import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Settings, Globe, Briefcase, Building2, Plus, Edit, Trash2, 
  RefreshCw, Loader2, DollarSign, Phone, Flag, Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SystemConfig = () => {
  const [activeTab, setActiveTab] = useState('countries');
  const [countries, setCountries] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'country', 'job_title', 'department'
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const { getAuthHeaders } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/system-config/countries?include_inactive=true`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCountries(data);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchJobTitles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/system-config/job-titles?include_inactive=true`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setJobTitles(data);
      }
    } catch (error) {
      console.error('Error fetching job titles:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/system-config/departments?include_inactive=true`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCountries(), fetchJobTitles(), fetchDepartments()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const response = await fetch(`${API_URL}/api/system-config/seed-defaults`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        await Promise.all([fetchCountries(), fetchJobTitles(), fetchDepartments()]);
      }
    } catch (error) {
      console.error('Error seeding defaults:', error);
    } finally {
      setSeeding(false);
    }
  };

  const openAddModal = (type) => {
    setModalType(type);
    setEditItem(null);
    setFormData({});
    setError('');
    setShowModal(true);
  };

  const openEditModal = (type, item) => {
    setModalType(type);
    setEditItem(item);
    setFormData({ ...item });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      let endpoint = '';
      let method = editItem ? 'PUT' : 'POST';
      let bodyData = {};
      
      switch (modalType) {
        case 'country':
          endpoint = editItem 
            ? `${API_URL}/api/system-config/countries/${editItem.id}`
            : `${API_URL}/api/system-config/countries`;
          // Only send the fields expected by the API
          bodyData = {
            name: formData.name,
            code: formData.code,
            currency_code: formData.currency_code,
            currency_symbol: formData.currency_symbol,
            phone_prefix: formData.phone_prefix
          };
          if (editItem && formData.is_active !== undefined) {
            bodyData.is_active = formData.is_active;
          }
          break;
        case 'job_title':
          endpoint = editItem
            ? `${API_URL}/api/system-config/job-titles/${editItem.id}`
            : `${API_URL}/api/system-config/job-titles`;
          bodyData = {
            title: formData.title,
            department: formData.department || null,
            level: formData.level || null,
            description: formData.description || null
          };
          if (editItem && formData.is_active !== undefined) {
            bodyData.is_active = formData.is_active;
          }
          break;
        case 'department':
          endpoint = editItem
            ? `${API_URL}/api/system-config/departments/${editItem.id}`
            : `${API_URL}/api/system-config/departments`;
          bodyData = {
            name: formData.name,
            description: formData.description || null
          };
          if (editItem && formData.is_active !== undefined) {
            bodyData.is_active = formData.is_active;
          }
          break;
      }

      const response = await fetch(endpoint, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(bodyData)
      });

      if (response.ok) {
        setShowModal(false);
        if (modalType === 'country') await fetchCountries();
        if (modalType === 'job_title') await fetchJobTitles();
        if (modalType === 'department') await fetchDepartments();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Operation failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to deactivate this item?')) return;

    try {
      let endpoint = '';
      switch (type) {
        case 'country':
          endpoint = `${API_URL}/api/system-config/countries/${id}`;
          break;
        case 'job_title':
          endpoint = `${API_URL}/api/system-config/job-titles/${id}`;
          break;
        case 'department':
          endpoint = `${API_URL}/api/system-config/departments/${id}`;
          break;
      }

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        if (type === 'country') await fetchCountries();
        if (type === 'job_title') await fetchJobTitles();
        if (type === 'department') await fetchDepartments();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleReactivate = async (type, id) => {
    try {
      let endpoint = '';
      switch (type) {
        case 'country':
          endpoint = `${API_URL}/api/system-config/countries/${id}`;
          break;
        case 'job_title':
          endpoint = `${API_URL}/api/system-config/job-titles/${id}`;
          break;
        case 'department':
          endpoint = `${API_URL}/api/system-config/departments/${id}`;
          break;
      }

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: true })
      });

      if (response.ok) {
        if (type === 'country') await fetchCountries();
        if (type === 'job_title') await fetchJobTitles();
        if (type === 'department') await fetchDepartments();
      }
    } catch (error) {
      console.error('Error reactivating item:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="config-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="system-config-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">System Configuration</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Configure system-wide settings for departments, job titles, and operating countries
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Operating Countries</p>
                <p className="text-2xl font-bold">{countries.filter(c => c.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Briefcase className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Job Titles</p>
                <p className="text-2xl font-bold">{jobTitles.filter(j => j.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building2 className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Departments</p>
                <p className="text-2xl font-bold">{departments.filter(d => d.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="countries" data-testid="tab-countries">
            <Globe className="h-4 w-4 mr-2" />
            Countries
          </TabsTrigger>
          <TabsTrigger value="job_titles" data-testid="tab-job-titles">
            <Briefcase className="h-4 w-4 mr-2" />
            Job Titles
          </TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments">
            <Building2 className="h-4 w-4 mr-2" />
            Departments
          </TabsTrigger>
        </TabsList>

        {/* Countries Tab */}
        <TabsContent value="countries" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Operating Countries</h2>
            <Button onClick={() => openAddModal('country')} data-testid="add-country-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Country
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {countries.map(country => (
              <Card key={country.id} className={`${!country.is_active ? 'opacity-60' : ''}`} data-testid={`country-card-${country.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Flag className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{country.name}</h3>
                        <p className="text-sm text-gray-500">{country.code}</p>
                      </div>
                    </div>
                    {!country.is_active && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      <span>{country.currency_code} ({country.currency_symbol})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <span>{country.phone_prefix}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditModal('country', country)}>
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {country.is_active ? (
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete('country', country.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="text-green-600" onClick={() => handleReactivate('country', country.id)}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {countries.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No countries configured</h3>
                <p className="text-gray-600 mb-4">Add operating countries or load defaults</p>
                <Button onClick={() => openAddModal('country')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Country
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Job Titles Tab */}
        <TabsContent value="job_titles" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Job Titles</h2>
            <Button onClick={() => openAddModal('job_title')} data-testid="add-job-title-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Job Title
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobTitles.map(jt => (
              <Card key={jt.id} className={`${!jt.is_active ? 'opacity-60' : ''}`} data-testid={`job-title-card-${jt.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Briefcase className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{jt.title}</h3>
                        {jt.level && <Badge variant="outline" className="text-xs">{jt.level}</Badge>}
                      </div>
                    </div>
                    {!jt.is_active && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  {jt.department && (
                    <p className="text-sm text-gray-500 mb-2">Department: {jt.department}</p>
                  )}
                  {jt.description && (
                    <p className="text-sm text-gray-600 mb-4">{jt.description}</p>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditModal('job_title', jt)}>
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {jt.is_active ? (
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete('job_title', jt.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="text-green-600" onClick={() => handleReactivate('job_title', jt.id)}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {jobTitles.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No job titles configured</h3>
                <p className="text-gray-600 mb-4">Add job titles or load defaults</p>
                <Button onClick={() => openAddModal('job_title')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Job Title
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Departments Tab */}
        <TabsContent value="departments" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Department Templates</h2>
            <Button onClick={() => openAddModal('department')} data-testid="add-department-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Department
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {departments.map(dept => (
              <Card key={dept.id} className={`${!dept.is_active ? 'opacity-60' : ''}`} data-testid={`department-card-${dept.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{dept.name}</h3>
                      </div>
                    </div>
                    {!dept.is_active && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  {dept.description && (
                    <p className="text-sm text-gray-600 mb-4">{dept.description}</p>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditModal('department', dept)}>
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {dept.is_active ? (
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete('department', dept.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="text-green-600" onClick={() => handleReactivate('department', dept.id)}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {departments.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No departments configured</h3>
                <p className="text-gray-600 mb-4">Add departments or load defaults</p>
                <Button onClick={() => openAddModal('department')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Department
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md" data-testid="config-modal">
          <DialogHeader>
            <DialogTitle>
              {editItem ? 'Edit' : 'Add'} {modalType === 'country' ? 'Country' : modalType === 'job_title' ? 'Job Title' : 'Department'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Country Form */}
            {modalType === 'country' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Country Name *</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Tanzania"
                    required
                    data-testid="country-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Country Code *</Label>
                    <Input
                      id="code"
                      value={formData.code || ''}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., TZ"
                      maxLength={2}
                      required
                      data-testid="country-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone_prefix">Phone Prefix *</Label>
                    <Input
                      id="phone_prefix"
                      value={formData.phone_prefix || ''}
                      onChange={(e) => setFormData({ ...formData, phone_prefix: e.target.value })}
                      placeholder="e.g., +255"
                      required
                      data-testid="phone-prefix-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="currency_code">Currency Code *</Label>
                    <Input
                      id="currency_code"
                      value={formData.currency_code || ''}
                      onChange={(e) => setFormData({ ...formData, currency_code: e.target.value.toUpperCase() })}
                      placeholder="e.g., TZS"
                      maxLength={3}
                      required
                      data-testid="currency-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currency_symbol">Currency Symbol *</Label>
                    <Input
                      id="currency_symbol"
                      value={formData.currency_symbol || ''}
                      onChange={(e) => setFormData({ ...formData, currency_symbol: e.target.value })}
                      placeholder="e.g., TSh"
                      required
                      data-testid="currency-symbol-input"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Job Title Form */}
            {modalType === 'job_title' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Software Engineer"
                    required
                    data-testid="job-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="level">Level</Label>
                    <Select
                      value={formData.level || ''}
                      onValueChange={(value) => setFormData({ ...formData, level: value })}
                    >
                      <SelectTrigger data-testid="job-level-select">
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Entry">Entry</SelectItem>
                        <SelectItem value="Mid">Mid</SelectItem>
                        <SelectItem value="Senior">Senior</SelectItem>
                        <SelectItem value="Executive">Executive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Select
                      value={formData.department || ''}
                      onValueChange={(value) => setFormData({ ...formData, department: value })}
                    >
                      <SelectTrigger data-testid="job-department-select">
                        <SelectValue placeholder="Any department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.filter(d => d.is_active).map(dept => (
                          <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this role"
                    data-testid="job-description-input"
                  />
                </div>
              </>
            )}

            {/* Department Form */}
            {modalType === 'department' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Department Name *</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Human Resources"
                    required
                    data-testid="dept-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description"
                    data-testid="dept-description-input"
                  />
                </div>
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-config-button">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editItem ? 'Update' : 'Create'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemConfig;
